import { useRef, useState } from "react";

import { Toolbar, ToolBarTheme } from "@impero-com/common/components/toolbar";
import type { ToolSpec } from "@impero-com/common/components/toolbar";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { set } from "@impero-com/common/utils/obj";
import {
    DEFAULT_PAGE_SIZE,
    MIN_PAGINATION_ROWS,
    Pagination,
} from "@impero-com/ui";

import {
    Table,
    TableProps,
    TableStyles,
    TableTheme,
    DefaultTableStyles,
} from "components/table";

export type { ToolSpec } from "@impero-com/common/components/toolbar";

export const DefaultTableWithToolBarStyles: TableStyles = set(
    DefaultTableStyles,
    {
        tableClassName:
            TableTheme.tableDefault + " " + TableTheme.sortableTable,
    }
);

export const DefaultEditTableWithToolBarStyles: TableStyles = set(
    DefaultTableStyles,
    {
        tableClassName:
            TableTheme.tableDefault +
            " " +
            TableTheme.sortableTable +
            " " +
            TableTheme.editTable,
    }
);

export interface TableWithToolBarProps<Row> extends TableProps<Row> {
    toolbarClassName?: string;
    floatingToolbar?: boolean;
    tools: Array<ToolSpec>;
}
export function TableWithToolBar<Row>({
    styles,
    rows,
    toolbarClassName,
    floatingToolbar,
    tools,
    ...restProps
}: TableWithToolBarProps<Row>) {
    const { t } = useTranslation();
    const showPagination = rows.length >= MIN_PAGINATION_ROWS;
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const shownFrom = page * pageSize;
    const shownTo = showPagination ? (page + 1) * pageSize : rows.length;

    const realToolbarClassName =
        toolbarClassName || ToolBarTheme.reactableToolbar;
    const toolbarClassNames: Array<string> = [realToolbarClassName];
    const realStyles: TableStyles = styles || DefaultTableWithToolBarStyles;
    const contentRef = useRef<HTMLDivElement>(null);
    const shownRows = rows.slice(shownFrom, shownTo);

    return (
        <>
            <Toolbar
                tools={tools}
                floatingToolbar={floatingToolbar}
                toolbarClassName={toolbarClassNames.join(" ")}
            />
            <div ref={contentRef}>
                <Table {...restProps} rows={shownRows} styles={realStyles} />
            </div>
            <Pagination
                tableElement={contentRef}
                totalRowCount={rows.length}
                pageSize={pageSize}
                current={page}
                onChangePage={(newPage: number) => setPage(newPage)}
                onPageSizeChange={(newPageSize: number) => {
                    setPageSize(newPageSize);
                    setPage(0);
                }}
                pageLabel={`${shownFrom + 1}-${Math.min(
                    shownTo,
                    rows.length
                )} of ${rows.length}`}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
            />
        </>
    );
}
